import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CountriesList from '../../services/countries.service';
import { setCountriesBalances, confirmTransferMoney } from '../../actions/bank';
import { filterData } from '../../actions/filterData';
import { useTranslation } from 'react-i18next';
import usePhoneCheck from '../../hooks/usePhoneCheck';
import BankMobileBody from '../../components/ComponentTable/Table/BankMobileBody';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import BankTableBody from '../../components/ComponentTable/Table/BankTableBody';
import BankTableHead from '../../components/ComponentTable/Table/BankTableHead';
import BankTableToolbar from '../../components/ComponentTable/Table/BankTableToolbar';
import BackButton from '../../components/BalanceComponent/BackButton';
import { Stack, Box, Table, TableContainer, Paper } from '@mui/material';
import { toast } from 'react-toastify';

const headCells = [
    {
        id: 'checkbox',
        numeric: false,
        disablePadding: false,
        label: '',
        type: 'checkbox',
    },
    {
        id: 'rowId',
        numeric: false,
        disablePadding: false,
        label: 'ID',
        type: 'string',
    },
    {
        id: 'country',
        numeric: false,
        disablePadding: false,
        label: 'table.country',
        type: 'string',
    },
    {
        id: 'surname',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.surname',
        type: 'string',
    },
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.name',
        type: 'string',
    },
    {
        id: 'balance',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.balance',
        type: 'string',
    },
    {
        id: 'quantity',
        numeric: true,
        disablePadding: false,
        label: 'bankFranchPage.quantity',
        type: 'decimalInput',
    },
    {
        id: 'bankAccount',
        numeric: true,
        disablePadding: false,
        label: 'bankFranchPage.bankAccount',
        type: 'link',
    },
];

function getRowData(data, countries, currentLanguage) {
    return data
        ? data.map(item => {
              const country = countries?.find(el => el.boss_id === item.id);

              let countryName = country?.lang_def?.[currentLanguage] || '';
              if (!countryName && country?.lang_def) {
                  const fallbackKey = Object.keys(country.lang_def).find(key => country.lang_def[key]);
                  countryName = fallbackKey ? country.lang_def[fallbackKey] : '';
              }

              return {
                  error: false,
                  rowId: String(item.id),
                  name: item.first_name || '',
                  surname: item.last_name || '',
                  country: countryName,
                  balance: Number(item.balance),
                  quantity: 10000,
              };
          })
        : [];
}

export default function BossBalances() {
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorStates, setErrorStates] = useState({});
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [countries, setCountries] = useState([]);
    const [filters, setFilters] = useState({
        rowId: '',
        surname: '',
        country: '',
    });

    const mobile = usePhoneCheck();
    const history = useHistory();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const user = useSelector(state => state.bank);
    const balance = user.customer.balance || 0;

    const getCountries = async () => {
        const response = await CountriesList.CountriesList(false, { size: 9999 });
        const list = response?.list || [];
        const result = list.map(({ boss_id, lang_def, title }) => ({
            boss_id,
            lang_def,
            title,
        }));
        setCountries(result);
    };

    useEffect(() => {
        getCountries();
    }, []);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const countriesBalancesInfo = useMemo(() => {
        return getRowData(user.customer.countriesBalances, countries, currentLanguage);
    }, [user, countries, currentLanguage]);

    useEffect(() => {
        setRows(countriesBalancesInfo);
    }, [countriesBalancesInfo]);

    const [rows, setRows] = useState(countriesBalancesInfo);

    const fetchCountriesBalances = async () => {
        await dispatch(setCountriesBalances());
    };

    useEffect(() => {
        fetchCountriesBalances();
    }, []);

    const handleQuantityChange = (id, value) => {
        const rowIndex = rows.findIndex(row => row.rowId === id);
        const newRows = [...rows];
        newRows[rowIndex] = {
            ...newRows[rowIndex],
            quantity: parseFloat(value) || '',
        };

        setRows(newRows);
        setErrorStates(prevState => ({
            ...prevState,
            [id]: value === '',
        }));
    };

    const handleSelectAllClick = event => {
        if (loading) return;
        if (event.target.checked) {
            const newSelected = rows.map(n => n.rowId);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        if (loading) return;

        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = [...selected, id];
        } else if (selectedIndex >= 0) {
            newSelected = selected.filter(itemId => itemId !== id);
        }

        setSelected(newSelected);
        setErrorStates(prevState => ({
            ...prevState,
            [id]: false,
        }));
    };

    const isSelected = id => selected.indexOf(id) !== -1;

    async function handleSuccessClick() {
        await confirmTransferMoney({
            rows,
            isSelected,
            toast,
            t,
            balance,
            updateBalanceFunction: fetchCountriesBalances,
            getCustomerId: el => el.rowId,
            setSelected,
        });
    }

    const handleFilterChange = (key, value) => {
        if (loading) return;
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setRows(filterData(countriesBalancesInfo, newFilters));
    };

    const filterConfigs = [
        { label: 'ID', key: 'rowId' },
        { label: t('bankFranchPage.surname'), key: 'surname' },
        { label: t('table.country'), key: 'country' },
    ];

    const handleBankAccountClick = id => {
        history.push(`/users/bank/${id}/boss`);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <BackButton />
            <Paper sx={{ width: '100%', mb: 2, p: 3 }}>
                <Stack direction={mobile ? 'column' : 'row'} justifyContent="flex-start" spacing={2}>
                    {filterConfigs.map(({ label, key, transform }) => (
                        <CustomAutocomplete
                            key={key}
                            label={label}
                            options={[...new Set(rows.map(row => (transform ? transform(row[key]) : row[key]) || ''))]}
                            inputValue={filters[key] || ''}
                            onChange={(e, newValue) => handleFilterChange(key, newValue || '')}
                            onInputChange={(e, newInputValue) =>
                                setFilters(prev => ({ ...prev, [key]: newInputValue || '' }))
                            }
                        />
                    ))}
                </Stack>
                <BankTableToolbar
                    title={t('bankFranchPage.selected')}
                    titleTooltipSuccess={t('bankStudentsPage.tooltipTitle')}
                    numSelected={selected.length}
                    onSuccessClick={handleSuccessClick}
                    loading={loading}
                    selectedUserIds={selected}
                    rows={rows}
                />
                {mobile ? (
                    <BankMobileBody
                        rows={rows}
                        headCells={headCells}
                        isSelected={isSelected}
                        handleSelectAllClick={handleSelectAllClick}
                        handleQuantityChange={handleQuantityChange}
                        handleClick={handleClick}
                        handleBankAccountClick={handleBankAccountClick}
                        mobile={mobile}
                    />
                ) : (
                    <TableContainer style={{ maxHeight: '59vh' }}>
                        <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                            <BankTableHead
                                numSelected={selected.length}
                                onSelectAllClick={handleSelectAllClick}
                                headCells={headCells}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                            />
                            <BankTableBody
                                rows={rows}
                                headCells={headCells}
                                isSelected={isSelected}
                                handleClick={handleClick}
                                handleQuantityChange={handleQuantityChange}
                                handleBankAccountClick={handleBankAccountClick}
                                order={order}
                                orderBy={orderBy}
                            />
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}
